import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { 
  Wallet, Banknote, CreditCard, Landmark, Download, Loader2, Calendar, FileText, Search, Building2, Filter, Printer
} from 'lucide-react';
import PaymentModal from '../../components/shared/PaymentModal';
import ReceiptTemplate from '../../components/shared/ReceiptTemplate';
import { canViewBranchData } from '../../utils/branchUtils';

export default function PaymentManagement() {
  const { isSuperAdmin, userProfile, currentUser, systemSettings } = useAuth();
  
  const [payments, setPayments] = useState([]);
  const [shares, setShares] = useState({});
  const [animals, setAnimals] = useState({});
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Filtreler
  const [searchTerm, setSearchTerm] = useState('');
  const [activeParentTab, setActiveParentTab] = useState('all');
  const [activeSubTab, setActiveSubTab] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');

  // Modal State
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedShareObj, setSelectedShareObj] = useState(null);
  const [selectedReceiptInfo, setSelectedReceiptInfo] = useState(null);

  useEffect(() => {
    // 1. Hayvanları Çek (Şube tespiti ve güvenlik filtresi)
    const systemBranches = systemSettings?.systemBranches || [];
    const unSubAnimals = onSnapshot(query(collection(db, 'animals')), (snapshot) => {
      const animalsDict = {};
      snapshot.forEach(doc => {
        const data = doc.data();
        const isBranchAllowed = canViewBranchData(userProfile, data.branch, systemBranches);
        if (isBranchAllowed) {
           animalsDict[doc.id] = { id: doc.id, ...data };
        }
      });
      setAnimals(animalsDict);
    });

    // 2. Hisseleri Çek
    const unSubShares = onSnapshot(query(collection(db, 'shares')), (snapshot) => {
      const sharesDict = {};
      snapshot.forEach(doc => {
        sharesDict[doc.id] = { id: doc.id, ...doc.data() };
      });
      setShares(sharesDict);
    });

    // 3. Ödemeleri Çek
    const unSubPayments = onSnapshot(query(collection(db, 'payments'), orderBy('createdAt', 'desc')), (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPayments(data);
      setLoading(false);
    });

    // 4. Admin/Kullanıcıları Çek
    const unSubUsers = onSnapshot(query(collection(db, 'users')), (snapshot) => {
      const usersDict = {};
      snapshot.forEach(doc => {
        usersDict[doc.id] = { id: doc.id, ...doc.data() };
      });
      setUsers(usersDict);
    });

    return () => {
      unSubAnimals();
      unSubShares();
      unSubPayments();
      unSubUsers();
    };
  }, []);

  // Şube Listesi (Benzersiz)
  const branchList = useMemo(() => {
    const branches = new Set();
    Object.values(animals).forEach(a => {
      if (a.branch) branches.add(a.branch);
    });
    return Array.from(branches);
  }, [animals]);

  // --- FİLTRELEME MANTIĞI ---
  const filteredPayments = useMemo(() => {
    return payments.filter(p => {
      const parentShare = shares[p.shareId];
      if (!parentShare) return false;
      const parentAnimal = animals[parentShare.animalId];
      if (!parentAnimal) return false;

      // 1. Şube (Branch / Hiyerarşik) Filtresi
      if (activeParentTab !== 'all') {
        const branchObj = systemSettings?.systemBranches?.find(b => b.id === parentAnimal.branch);
        const belongsToParent = parentAnimal.branch === activeParentTab || branchObj?.parentId === activeParentTab;
        if (!belongsToParent) return false;

        if (activeSubTab !== 'all') {
          if (parentAnimal.branch !== activeSubTab) return false;
        }
      }

      // 2. Tarih Filtresi
      if (selectedDate) {
        const paymentDateObj = p.createdAt?.toDate();
        if (paymentDateObj) {
          // yerel YYYY-MM-DD string'i elde etmek için timezone ayarlaması
          const pYear = paymentDateObj.getFullYear();
          const pMo = String(paymentDateObj.getMonth() + 1).padStart(2, '0');
          const pDa = String(paymentDateObj.getDate()).padStart(2, '0');
          const pDateStr = `${pYear}-${pMo}-${pDa}`;
          if (pDateStr !== selectedDate) return false;
        } else {
          return false; // Tarihi olmayan makbuz
        }
      }

      // 3. İsim / Telefon / Admin / Makbuz Araması
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const fullName = `${parentShare.customerFirstName} ${parentShare.customerLastName}`.toLowerCase();
        const phone = parentShare.customerPhone || '';
        const receiptNo = p.receiptNo ? String(p.receiptNo).toLowerCase() : '';
        const adminName = (users[p.createdBy]?.username || users[p.createdBy]?.email?.split('@')[0] || (p.createdBy ? 'admin' : 'sistem')).toLowerCase();

        if (
          !fullName.includes(searchLower) && 
          !phone.includes(searchLower) && 
          !receiptNo.includes(searchLower) && 
          !adminName.includes(searchLower)
        ) {
          return false;
        }
      }

      // 4. Ödeme Yöntemi Filtresi
      if (paymentMethodFilter !== 'all' && p.type !== paymentMethodFilter) {
        return false;
      }

      return true;
    });
  }, [payments, shares, animals, users, activeParentTab, activeSubTab, selectedDate, searchTerm, systemSettings, paymentMethodFilter]);

  // --- KASA ÖZETLERİ HESABI ---
  let totalNakit = 0;
  let totalKrediKarti = 0;
  let totalHavale = 0;

  filteredPayments.forEach(p => {
    const amt = Number(p.amount) || 0;
    if (p.type === 'Nakit') totalNakit += amt;
    else if (p.type === 'Kredi Kartı') totalKrediKarti += amt;
    else if (p.type === 'Havale/EFT') totalHavale += amt;
  });

  const totalKasa = totalNakit + totalKrediKarti + totalHavale;

  // --- EXCEL CIKTISI ---
  const handleExportCSV = () => {
    const headers = ['Tarih', 'Saat', 'Sube', 'Musteri Ad Soyad', 'Telefon', 'Makbuz No', 'Odeme Tipi', 'Islemi Yapan', 'Tutar'];
    const rows = filteredPayments.map(p => {
      const s = shares[p.shareId];
      if (!s) return null;
      const a = animals[s.animalId];
      const branchStr = a ? a.branch.toUpperCase() : 'BİLİNMİYOR';
      const dateObj = p.createdAt ? p.createdAt.toDate() : null;
      const dateStr = dateObj ? dateObj.toLocaleDateString('tr-TR') : 'Bilinmiyor';
      const timeStr = dateObj ? dateObj.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
      const creator = users[p.createdBy]?.username || users[p.createdBy]?.email?.split('@')[0] || (p.createdBy ? 'Admin' : 'Sistem');
      
      return [
        dateStr,
        timeStr,
        branchStr,
        `${s.customerFirstName} ${s.customerLastName}`,
        s.customerPhone,
        p.receiptNo || '-',
        p.type,
        creator,
        p.amount
      ].join(',');
    }).filter(Boolean);

    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + headers.join(',') + '\n' + rows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Kasa_Defteri_${new Date().toLocaleDateString('tr-TR')}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Hissedara Tıklayınca Modal Açma İşlemi
  const handleOpenShareholder = (shareId) => {
    const s = shares[shareId];
    if (s) {
      setSelectedShareObj(s);
      setPaymentModalOpen(true);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      
      {/* Üst Başlık */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
            <Wallet className="w-6 h-6 text-gold-400" />
            Merkezi Kasa Defteri
          </h2>
          <p className="text-sm text-navy-400 mt-1">
            Sistemdeki tüm şubelerden akan tahsilatların resmi dökümü. (Müşteriye tıklayıp yönetebilirsiniz)
          </p>
        </div>
        <button 
          onClick={handleExportCSV}
          className="flex items-center gap-2 bg-navy-800 hover:bg-forest-600 border border-navy-600 hover:border-forest-500 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-all shadow-lg"
        >
          <Download className="w-4 h-4" />
          Excel (CSV) İndir
        </button>
      </div>

      {/* ŞÜBE FİLTRESİ (HİYERARŞİK SEKMELER) */}
      {(() => {
         const systemBranches = systemSettings?.systemBranches || [];
         const mainBranches = systemBranches.filter(b => !b.parentId && (branchList.includes(b.id) || systemBranches.some(sub => sub.parentId === b.id && branchList.includes(sub.id))));
         const currentSubBranches = activeParentTab !== 'all' ? systemBranches.filter(b => b.parentId === activeParentTab && branchList.includes(b.id)) : [];

         if (mainBranches.length === 0) return null;

         return (
            <div className="flex flex-col gap-2 relative z-10 w-full mb-6">
               <div className="flex items-center gap-2 bg-navy-900/60 p-1.5 rounded-xl border border-navy-700/50 backdrop-blur-md overflow-x-auto custom-scrollbar">
                  <Filter className="w-4 h-4 text-navy-400 ml-2 mr-1 shrink-0" />
                  <span className="text-xs font-semibold text-navy-400 mr-2 shrink-0">Şube:</span>
                  <button
                     onClick={() => { setActiveParentTab('all'); setActiveSubTab('all'); }}
                     className={`px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${activeParentTab === 'all' ? 'bg-gold-500 text-navy-950 shadow-lg scale-105' : 'text-navy-300 hover:text-white hover:bg-navy-800'}`}
                  >
                     TÜM SİSTEM
                  </button>
                  {mainBranches.map(b => (
                     <button
                        key={b.id}
                        onClick={() => { setActiveParentTab(b.id); setActiveSubTab('all'); }}
                        className={`px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap flex items-center gap-1.5 transition-all ${activeParentTab === b.id ? 'bg-navy-700 text-gold-400 shadow-lg border border-gold-500/30' : 'bg-navy-800 text-navy-300 hover:text-white hover:bg-navy-700'}`}
                     >
                        <Building2 className="w-3.5 h-3.5" />
                        {b.name.toUpperCase()}
                     </button>
                  ))}
               </div>

               {/* Alt Şube (Çocuk) Sekmeleri */}
               {currentSubBranches.length > 0 && (
                  <div className="flex items-center pl-10 gap-2 overflow-x-auto custom-scrollbar py-1 animate-in slide-in-from-top-2 fade-in relative before:content-[''] before:absolute before:left-6 before:top-0 before:bottom-3 before:w-px before:bg-navy-700">
                     <svg className="w-4 h-4 text-navy-600 absolute left-6 bottom-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2" />
                     </svg>
                     <button
                        onClick={() => setActiveSubTab('all')}
                        className={`px-3 py-1.5 rounded-lg text-[11px] font-bold whitespace-nowrap transition-all ${activeSubTab === 'all' ? 'bg-forest-600/20 text-forest-400 border border-forest-500/30' : 'bg-navy-800/50 text-navy-400 hover:text-white'}`}
                     >
                        Sadece Bu Merkez
                     </button>
                     {currentSubBranches.map(sub => (
                        <button
                           key={sub.id}
                           onClick={() => setActiveSubTab(sub.id)}
                           className={`px-3 py-1.5 rounded-lg text-[11px] font-bold whitespace-nowrap transition-all ${activeSubTab === sub.id ? 'bg-cyan-900/40 text-cyan-400 border border-cyan-500/30 shadow-md' : 'bg-navy-800 text-navy-400 hover:text-white'}`}
                        >
                           {sub.name}
                        </button>
                     ))}
                  </div>
               )}
            </div>
         );
      })()}

      {/* ÖDEME YÖNTEMİ FİLTRESİ */}
      <div className="flex flex-wrap items-center gap-2 bg-navy-900/60 p-1.5 rounded-xl border border-navy-700/50 backdrop-blur-md mb-6 w-fit">
          <Filter className="w-4 h-4 text-navy-400 ml-2 mr-1 shrink-0" />
          <span className="text-xs font-semibold text-navy-400 mr-2 shrink-0">Ödeme Tipi:</span>
          <button
            onClick={() => setPaymentMethodFilter('all')}
            className={`px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${paymentMethodFilter === 'all' ? 'bg-gold-500 text-navy-950 shadow-lg scale-105' : 'text-navy-300 hover:text-white hover:bg-navy-800'}`}
          >
            TÜMÜ
          </button>
          <button
            onClick={() => setPaymentMethodFilter('Nakit')}
            className={`px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${paymentMethodFilter === 'Nakit' ? 'bg-emerald-600 text-white shadow-lg' : 'text-emerald-400 hover:text-white hover:bg-navy-800'}`}
          >
            Nakit
          </button>
          <button
            onClick={() => setPaymentMethodFilter('Kredi Kartı')}
            className={`px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${paymentMethodFilter === 'Kredi Kartı' ? 'bg-blue-600 text-white shadow-lg' : 'text-blue-400 hover:text-white hover:bg-navy-800'}`}
          >
            Kredi Kartı
          </button>
          <button
            onClick={() => setPaymentMethodFilter('Havale/EFT')}
            className={`px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${paymentMethodFilter === 'Havale/EFT' ? 'bg-purple-600 text-white shadow-lg' : 'text-purple-400 hover:text-white hover:bg-navy-800'}`}
          >
            Havale / EFT
          </button>
      </div>

      {/* ÖZET KARTLAR */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass rounded-2xl p-5 border-t-2 border-t-gold-500 hover:bg-navy-800/30 transition-colors">
           <div className="flex items-center gap-3 mb-2">
             <div className="p-2 rounded-lg bg-gold-500/10"><Wallet className="w-5 h-5 text-gold-500" /></div>
             <p className="text-sm font-medium text-navy-300">Genel Toplam Kasa</p>
           </div>
           <p className="text-3xl font-bold text-white">{totalKasa.toLocaleString('tr-TR')} ₺</p>
        </div>

        <div className="glass rounded-2xl p-5 border-t-2 border-t-emerald-500 hover:bg-navy-800/30 transition-colors">
           <div className="flex items-center gap-3 mb-2">
             <div className="p-2 rounded-lg bg-emerald-500/10"><Banknote className="w-5 h-5 text-emerald-500" /></div>
             <p className="text-sm font-medium text-navy-300">Nakit</p>
           </div>
           <p className="text-2xl font-bold text-emerald-400">{totalNakit.toLocaleString('tr-TR')} ₺</p>
        </div>

        <div className="glass rounded-2xl p-5 border-t-2 border-t-blue-500 hover:bg-navy-800/30 transition-colors">
           <div className="flex items-center gap-3 mb-2">
             <div className="p-2 rounded-lg bg-blue-500/10"><CreditCard className="w-5 h-5 text-blue-500" /></div>
             <p className="text-sm font-medium text-navy-300">Kredi Kartı</p>
           </div>
           <p className="text-2xl font-bold text-blue-400">{totalKrediKarti.toLocaleString('tr-TR')} ₺</p>
        </div>

        <div className="glass rounded-2xl p-5 border-t-2 border-t-purple-500 hover:bg-navy-800/30 transition-colors">
           <div className="flex items-center gap-3 mb-2">
             <div className="p-2 rounded-lg bg-purple-500/10"><Landmark className="w-5 h-5 text-purple-500" /></div>
             <p className="text-sm font-medium text-navy-300">Havale / EFT</p>
           </div>
           <p className="text-2xl font-bold text-purple-400">{totalHavale.toLocaleString('tr-TR')} ₺</p>
        </div>
      </div>

      {/* DETAYLI LİSTE */}
      <div className="glass rounded-2xl border border-navy-700/50 overflow-hidden shadow-xl">
        <div className="px-5 py-4 border-b border-navy-700/50 bg-navy-900/50 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
           {/* Sol Taraf: İsim Araması */}
           <div className="relative w-full md:w-80">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-navy-400"><Search className="w-4 h-4" /></span>
              <input
                type="text" placeholder="Müşteri, Admin, Telefon veya Makbuz No ara..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-navy-800/60 border border-navy-600/30 text-white placeholder-navy-500 focus:border-forest-500 outline-none text-sm"
              />
           </div>
           
           {/* Sağ Taraf: Tarih Özeti */}
           <div className="flex items-center gap-3 w-full md:w-auto">
              <label className="text-xs font-semibold text-navy-300 truncate">Tarihe Göre:</label>
              <input 
                 type="date"
                 value={selectedDate}
                 onChange={e => setSelectedDate(e.target.value)}
                 className="bg-navy-800 text-white border border-navy-600 rounded-lg px-2 py-1.5 text-sm focus:border-forest-500 outline-none"
              />
              {selectedDate && (
                 <button onClick={() => setSelectedDate('')} className="text-xs text-danger-400 hover:underline">Temizle</button>
              )}
           </div>
        </div>
        
        {loading ? (
           <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 text-gold-500 animate-spin" /></div>
        ) : filteredPayments.length === 0 ? (
           <div className="flex flex-col items-center justify-center p-16 text-navy-400">
              <FileText className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-sm">Seçili kriterlere uygun makbuz bulunamadı.</p>
           </div>
        ) : (
           <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-left text-sm whitespace-nowrap">
                 <thead className="bg-navy-900/80 border-b border-navy-700/50">
                    <tr>
                       <th className="px-5 py-4 font-semibold text-navy-300">Tarih / Saat</th>
                       <th className="px-5 py-4 font-semibold text-navy-300">Şube</th>
                       <th className="px-5 py-4 font-semibold text-navy-300">Müşteri (Tahsilat Özeti İçin Tıkla)</th>
                       <th className="px-5 py-4 font-semibold text-navy-300">Yöntem</th>
                       <th className="px-5 py-4 font-semibold text-navy-300">İşlemi Yapan</th>
                       <th className="px-5 py-4 font-semibold text-navy-300 text-right">Tutar</th>
                    </tr>
                 </thead>
                 <tbody className="divide-y divide-navy-700/50">
                    {filteredPayments.map(payment => {
                       const share = shares[payment.shareId];
                       if (!share) return null;
                       const animal = animals[share.animalId];

                       let typeColors = "bg-navy-800 text-navy-300 border-navy-600";
                       if (payment.type === 'Nakit') typeColors = "bg-emerald-900/30 text-emerald-400 border-emerald-500/30";
                       if (payment.type === 'Kredi Kartı') typeColors = "bg-blue-900/30 text-blue-400 border-blue-500/30";
                       if (payment.type === 'Havale/EFT') typeColors = "bg-purple-900/30 text-purple-400 border-purple-500/30";

                       const dateObj = payment.createdAt ? payment.createdAt.toDate() : null;
                       const dateStr = dateObj ? dateObj.toLocaleDateString('tr-TR') : 'Bilinmiyor';
                       const timeStr = dateObj ? dateObj.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
                       const creator = users[payment.createdBy]?.username || users[payment.createdBy]?.email?.split('@')[0] || (payment.createdBy ? 'Admin' : 'Sistem');

                       return (
                          <tr key={payment.id} className="hover:bg-navy-800/60 transition-colors group">
                             <td className="px-5 py-4">
                                <div className="flex flex-col">
                                   <span className="flex items-center gap-2 text-navy-300 font-mono text-xs">
                                      <Calendar className="w-3.5 h-3.5 opacity-50"/> 
                                      {dateStr}
                                   </span>
                                   {timeStr && <span className="text-[10px] text-navy-400 mt-0.5 ml-5">{timeStr}</span>}
                                </div>
                             </td>
                             <td className="px-5 py-4">
                                <span className="text-xs font-semibold px-2 py-1 rounded bg-navy-800 text-navy-300 border border-navy-700">
                                   {animal ? animal.branch.toUpperCase() : 'YOK'}
                                </span>
                             </td>
                             <td className="px-5 py-4 cursor-pointer" onClick={() => handleOpenShareholder(payment.shareId)}>
                                <div className="flex flex-col group-hover:text-gold-400 transition-colors">
                                   <span className="font-bold text-white group-hover:text-gold-300 uppercase underline decoration-navy-600 decoration-dotted underline-offset-4">
                                      {share.customerFirstName} {share.customerLastName}
                                   </span>
                                   <span className="text-xs text-navy-400 font-mono mt-0.5">{share.customerPhone}</span>
                                </div>
                             </td>
                             <td className="px-5 py-4">
                                <span className={`px-2.5 py-1 rounded-md text-[11px] font-bold border ${typeColors}`}>
                                   {payment.type}
                                </span>
                             </td>
                             <td className="px-5 py-4">
                                <div className="flex items-center gap-2">
                                   <div className="w-6 h-6 rounded-full bg-navy-700 flex items-center justify-center text-[10px] font-bold text-white border border-navy-600">
                                      {creator.substring(0,2).toUpperCase()}
                                   </div>
                                   <span className="text-xs font-semibold text-navy-300">{creator}</span>
                                </div>
                             </td>
                             <td className="px-5 py-4 text-right">
                                <div className="flex flex-col items-end gap-1">
                                   <span className="font-extrabold text-gold-400 text-base">{payment.amount.toLocaleString('tr-TR')} ₺</span>
                                   {payment.receiptNo ? (
                                      <button 
                                         onClick={() => setSelectedReceiptInfo({ payment, liveShare: share, currentAnimal: animal })}
                                         className="text-[10px] text-navy-300 font-mono bg-navy-800 hover:bg-gold-500/10 hover:text-gold-400 px-1.5 py-0.5 rounded border border-navy-600 hover:border-gold-500/50 shadow-sm flex items-center gap-1 transition-all"
                                         title="Makbuzu Görüntüle ve Yazdır"
                                      >
                                         <Printer className="w-3 h-3" /> No: {payment.receiptNo}
                                      </button>
                                   ) : (
                                      <span className="text-[10px] text-navy-500/50 italic px-1.5 py-0.5">Makbuz Yok</span>
                                   )}
                                </div>
                             </td>
                          </tr>
                       )
                    })}
                 </tbody>
              </table>
           </div>
        )}
      </div>

      <PaymentModal 
         isOpen={paymentModalOpen} 
         onClose={() => setPaymentModalOpen(false)}
         liveShare={selectedShareObj}
         allAnimals={Object.values(animals)}
         isSuperAdmin={isSuperAdmin}
         currentUserUid={currentUser?.uid}
      />

      {selectedReceiptInfo && (
         <ReceiptTemplate 
            payment={selectedReceiptInfo.payment} 
            liveShare={selectedReceiptInfo.liveShare} 
            currentAnimal={selectedReceiptInfo.currentAnimal}
            onClose={() => setSelectedReceiptInfo(null)}
         />
      )}

    </div>
  );
}
